import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { ErrorState } from '@/components/ui/ErrorState';
import { Skeleton } from '@/components/ui/Skeleton';
import { SectionCard } from '@/components/common/SectionCard';
import { Avatar } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { ArrowLeftIcon } from '@/assets/icons/ArrowLeftIcon';
import { CheckIcon } from '@/assets/icons/CheckIcon';
import { CalendarIcon } from '@/assets/icons/CalendarIcon';
import { MapPinIcon } from '@/assets/icons/MapPinIcon';
import { ClockIcon } from '@/assets/icons/ClockIcon';
import { formatCardDateLabel, formatEventTimeRange, locationLabel } from '@/lib/utils/event.utils';
import { initialsFromName } from '@/lib/utils/user.utils';
import { UnregisterConfirmDialog } from './components/UnregisterConfirmDialog';
import { GuestRegisterCTA } from './components/GuestRegisterCTA';
import { GuestUnregisterCTA } from './components/GuestUnregisterCTA';
import { JoinDetailsPanel } from './components/JoinDetailsPanel';
import { EventTrustedPartner } from './components/EventTrustedPartner';
import { EventFactTile } from './components/EventFactTile';
import { EVENTS_STRINGS } from './constants/events-strings';
import { resolveEventBackLink, type EventBackLink } from './event-detail-back';
import { useNonMemberRegistration, type ViewerStatus } from './hooks/useNonMemberRegistration';
import { getApiErrorCode } from '@/lib/utils/error.utils';
import { API_ERROR_CODES } from '@/lib/constants/error-codes';
import { useAuth } from '@/hooks/useAuth';
import { useEventDetail, useEventRegistrationStatus, useRegisterForEvent, useUnregisterFromEvent } from '@/hooks/useEvents';

function RegisterCTA({
  eventId,
  isRegistered,
  isClosed,
  isAuthenticated,
  guestToken,
  onGuestChanged,
}: {
  eventId: string;
  isRegistered: boolean;
  isClosed: boolean;
  /** False for a visitor who arrived from the public Home events section. */
  isAuthenticated: boolean;
  /** The logged-out visitor's remembered token, for Unregister; null when none. */
  guestToken: string | null;
  /** Re-read the browser's remembered registration after a visitor registered or unregistered. */
  onGuestChanged: () => void;
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { mutate: register, isPending: isRegistering } = useRegisterForEvent(eventId);
  const { mutate: unregister, isPending: isUnregistering } = useUnregisterFromEvent(eventId);

  // A logged-out visitor registers with a name and email in a modal (FR-282), not
  // via login. Once the API confirms the remembered registration they can cancel it
  // with Unregister (FR-289, which supersedes A-308); otherwise Register shows.
  if (!isAuthenticated) {
    if (isRegistered) {
      return guestToken ? <GuestUnregisterCTA eventId={eventId} token={guestToken} onUnregistered={onGuestChanged} /> : null;
    }
    return <GuestRegisterCTA eventId={eventId} isClosed={isClosed} onRegistered={onGuestChanged} />;
  }

  if (isRegistered) {
    return (
      <>
        <Button variant="chip-outline" className="w-full" onClick={() => setConfirmOpen(true)}>
          Unregister
        </Button>
        <UnregisterConfirmDialog
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
          onConfirm={() => {
            unregister(undefined, { onSuccess: () => setConfirmOpen(false) });
          }}
          isPending={isUnregistering}
        />
      </>
    );
  }
  if (isClosed) {
    return (
      <Button variant="primary" className="w-full" disabled>
        {EVENTS_STRINGS.DETAIL.REGISTRATION_CLOSED}
      </Button>
    );
  }
  return (
    <Button variant="chip-green" className="w-full" onClick={() => register()} disabled={isRegistering}>
      {isRegistering ? 'Registering…' : 'Register'}
    </Button>
  );
}

const S = EVENTS_STRINGS.DETAIL;
/** Shared by the cover and the cards below it so their edges line up. */
const CONTENT_WIDTH = 'mx-auto w-full max-w-[87.5rem]';
/** Pulls the ticket card up over the bottom of the cover. */
const CARD_INSET = 'relative mx-4 sm:mx-8';

function BackBar({ to, label }: EventBackLink) {
  return (
    <div className="border-b border-divider bg-white px-page-x py-4">
      <Link
        to={to}
        className="inline-flex items-center gap-2 text-sm font-medium text-green transition-colors hover:text-dkgreen"
      >
        <ArrowLeftIcon size={14} />
        {label}
      </Link>
    </div>
  );
}

function EventDetailSkeleton() {
  return (
    <div className="flex min-h-full flex-col bg-events-bg" aria-label="Loading event details">
      <div className="border-b border-divider bg-white px-page-x py-4">
        <Skeleton className="h-6 w-32" />
      </div>
      <div className={`${CONTENT_WIDTH} flex flex-col px-page-x py-8`}>
        <Skeleton className="h-[23.75rem] w-full rounded-3xl" />
        <div className={`${CARD_INSET} -mt-18 flex flex-col gap-6 rounded-[1.25rem] bg-white p-8 shadow-card`}>
          <div className="flex justify-between gap-6">
            <div className="flex flex-1 flex-col gap-3">
              <Skeleton className="h-9 w-3/4" />
              <Skeleton className="h-5 w-1/2" />
            </div>
            <Skeleton className="h-12 w-52 shrink-0" />
          </div>
          <Skeleton className="h-12 w-full" />
        </div>
      </div>
    </div>
  );
}

export function EventDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  // Wait rather than assume logged out while the session resolves (as ViewerAdaptiveLayout does).
  const viewer: ViewerStatus = isAuthLoading ? 'resolving' : isAuthenticated ? 'member' : 'guest';
  const back = resolveEventBackLink(useLocation().state, isAuthenticated);
  const { data, isLoading, isError, error, refetch } = useEventDetail(id);
  const { data: registrationStatus } = useEventRegistrationStatus(id, isAuthenticated);
  // The remembered non-member registration (FR-285, FR-286). A logged-in viewer
  // ignores it entirely and the account's own status decides.
  const guest = useNonMemberRegistration(id, viewer);
  // After the registration modal closes its Register button is gone, so focus would
  // fall to <body>; move it to the page heading instead.
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [focusHeading, setFocusHeading] = useState(false);
  useEffect(() => {
    if (!focusHeading) return;
    headingRef.current?.focus();
    setFocusHeading(false);
  }, [focusHeading]);

  if (isLoading) return <EventDetailSkeleton />;

  if (isError) {
    const is404 = getApiErrorCode(error) === API_ERROR_CODES.EVENT_NOT_FOUND;
    return (
      <div className="flex min-h-full flex-col bg-events-bg">
        <BackBar {...back} />
        <div className="px-page-x py-8">
          <ErrorState
            title={is404 ? 'Event not found' : 'Something went wrong'}
            description={
              is404
                ? 'This event may have been removed or is not yet published.'
                : 'We could not load the event. Please try again.'
            }
            onRetry={is404 ? undefined : () => void refetch()}
          />
        </div>
      </div>
    );
  }

  if (!data) return null;

  const isRegistered = isAuthenticated ? (registrationStatus?.isRegistered ?? false) : guest.isRegistered;
  // Join details come from whichever status read applies, and only for a registered viewer
  // (FR-286 for a non-member, FR-292 for a member); nothing for anyone not registered.
  const memberJoin = isAuthenticated && isRegistered ? registrationStatus : undefined;
  const joinLocationType = isAuthenticated ? memberJoin?.locationType : guest.locationType;
  const joinDetails = isAuthenticated ? memberJoin?.locationDetails : guest.locationDetails;
  const isRegistrationClosed = data.registrationClosesAt
    ? new Date() > new Date(data.registrationClosesAt)
    : false;
  const timeRange = formatEventTimeRange(data.startsAt, data.endsAt);

  return (
    <div className="flex min-h-full flex-col bg-events-bg">
      <BackBar {...back} />

      <div className={`${CONTENT_WIDTH} flex flex-col px-page-x py-8`}>
        {/* Clean cover: no tags or text over the photo (UX sheet v2). */}
        <div className="h-[23.75rem] overflow-hidden rounded-3xl bg-input-disabled">
          {/* No stock image when there is no cover (MYN-343): the neutral surface shows instead. */}
          {data.coverPhotoUrl && (
            <img src={data.coverPhotoUrl} alt="" className="h-full w-full object-cover" />
          )}
        </div>

        {/* Ticket card: title + CTA, then the facts strip. */}
        <section
          className={`${CARD_INSET} -mt-18 flex flex-col gap-7 rounded-[1.25rem] bg-white p-8 shadow-[0_8px_32px_0_rgba(0,25,40,0.10)]`}
        >
          <div className="flex flex-wrap items-start justify-between gap-6">
            <div className="flex min-w-0 flex-[1_1_26rem] flex-col gap-2">
              {isRegistered && (
                <span className="inline-flex w-fit items-center gap-1.5 rounded-[0.375rem] bg-green px-3 py-1.5 text-xs font-semibold text-white">
                  <CheckIcon />
                  {S.REGISTERED}
                </span>
              )}
              <h1
                ref={headingRef}
                tabIndex={-1}
                className="text-[2.125rem] leading-tight font-bold tracking-[-0.5px] text-navy focus:outline-none"
              >
                {data.title}
              </h1>
              {data.subtitle && <p className="text-[1rem] text-subtext">{data.subtitle}</p>}
            </div>
            <div className="flex flex-[0_1_16.25rem] flex-col gap-2">
              {/* The registration-close date is no longer shown to users
                  (MYN-283, FR-197) - only the Register button's own disabled
                  state communicates that registration has closed. */}
              {/* Nothing is offered while the session or a remembered token is still being
                  checked, so neither Register nor Unregister is a guess. */}
              {viewer !== 'resolving' && !guest.isChecking && (
                <RegisterCTA
                  eventId={id}
                  isRegistered={isRegistered}
                  isClosed={isRegistrationClosed}
                  isAuthenticated={isAuthenticated}
                  guestToken={guest.token}
                  onGuestChanged={() => {
                    guest.refresh();
                    setFocusHeading(true);
                  }}
                />
              )}
            </div>
          </div>

          <div className="grid grid-cols-[repeat(auto-fit,minmax(13.75rem,1fr))] gap-4 border-t border-divider pt-6">
            <EventFactTile
              icon={<CalendarIcon size={20} />}
              label={S.FACT_DATE}
              value={formatCardDateLabel(data.startsAt)}
            />
            <EventFactTile
              icon={<ClockIcon size={20} />}
              label={S.FACT_TIME}
              value={timeRange.time}
              note={timeRange.endsOn ? `${S.ENDS_PREFIX} ${timeRange.endsOn}` : undefined}
            />
            {data.locationType && (
              <EventFactTile
                icon={<MapPinIcon size={20} />}
                label={S.FACT_LOCATION}
                value={locationLabel(data.locationType)}
              />
            )}
            {/* organiserEmail is deliberately never shown on this page. */}
            <EventFactTile
              icon={<Avatar initials={initialsFromName(data.organiserName)} size={44} />}
              label={S.FACT_ORGANIZER}
              value={data.organiserName}
            />
          </div>
        </section>

        {joinDetails && (
          <JoinDetailsPanel
            locationType={joinLocationType ?? data.locationType}
            locationDetails={joinDetails}
            className={`${CARD_INSET} mt-6`}
          />
        )}

        {(data.description || data.trustedPartner) && (
          <SectionCard title={S.ABOUT} className={`${CARD_INSET} mt-6`}>
            {/* Two columns once a partner is credited, description keeping two
                thirds; the description spans the full width on its own when
                there is no partner to show (FR-281, A-334). */}
            <div className="grid gap-6 lg:grid-cols-3">
              {data.description && (
                <p
                  className={`text-[1rem] leading-relaxed text-subtext ${
                    data.trustedPartner ? 'lg:col-span-2' : 'max-w-[47.5rem] lg:col-span-3'
                  }`}
                >
                  {data.description}
                </p>
              )}
              {data.trustedPartner && (
                <EventTrustedPartner
                  partner={data.trustedPartner}
                  isAuthenticated={isAuthenticated}
                />
              )}
            </div>
          </SectionCard>
        )}
      </div>
    </div>
  );
}
